// Google Analytics 4 helper module

/**
 * Check if gtag is available
 */
function isGtagReady() {
    return typeof gtag === 'function';
}

/**
 * Track custom events
 */
export function trackEvent(eventName, parameters = {}) {
    if (!isGtagReady()) return;
    gtag('event', eventName, parameters);
}

/**
 * Track virtual page views (for SPA-like navigation)
 */
export function trackPageView(pagePath, pageTitle) {
    if (!isGtagReady()) return;
    gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle,
        page_location: window.location.href
    });
}

/**
 * Set location context as user properties (persists across events)
 */
export function setLocationContext(region, area, station) {
    if (!isGtagReady()) return;
    gtag('set', 'user_properties', {
        current_region: region || '',
        current_area: area || '',
        current_station: station || ''
    });
}

/**
 * Initialize scroll depth tracking
 * Fires events at 25%, 50%, 75%, 90% thresholds (once per page load)
 */
export function initScrollDepthTracking() {
    const thresholds = [25, 50, 75, 90];
    const fired = new Set();

    window.addEventListener('scroll', () => {
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return;

        const scrollPercent = Math.round((window.scrollY / docHeight) * 100);

        thresholds.forEach(threshold => {
            if (scrollPercent >= threshold && !fired.has(threshold)) {
                fired.add(threshold);
                trackEvent('scroll_depth', {
                    percent_scrolled: threshold,
                    page_path: window.location.pathname
                });
            }
        });
    }, { passive: true });
}

// Pre-defined event helpers for common actions
export const analytics = {
    // Location selection (enhanced with conditions data)
    locationChange: (region, area, station, conditions = {}) => trackEvent('location_change', {
        region_name: region,
        area_name: area,
        station_name: station,
        swell_height_ft: conditions.swellHeight ?? null,
        swell_period_sec: conditions.swellPeriod ?? null,
        water_temp_f: conditions.waterTemp ?? null,
        wind_speed_mph: conditions.windSpeed ?? null,
        tide_state: conditions.tideState ?? null
    }),

    // Theme toggle
    themeToggle: (theme) => trackEvent('theme_toggle', {
        theme_selected: theme
    }),

    // Email signup
    signupStart: () => trackEvent('signup_start'),
    signupSuccess: (notificationType) => trackEvent('signup_success', {
        notification_type: notificationType
    }),
    signupError: (errorMessage) => trackEvent('signup_error', {
        error_message: errorMessage
    }),

    // Map interactions
    mapStationClick: (stationName, stationId) => trackEvent('map_station_click', {
        station_name: stationName,
        station_id: stationId
    }),

    // Data views
    viewTideChart: () => trackEvent('view_tide_chart'),

    // Rating calculated (tracks score when conditions are displayed)
    ratingCalculated: (score, category, stationId) => trackEvent('rating_calculated', {
        rating_score: score,
        rating_category: category,
        station_id: stationId
    }),

    // Scroll depth tracking
    scrollDepth: (percent) => trackEvent('scroll_depth', {
        percent_scrolled: percent,
        page_path: window.location.pathname
    }),

    // Chart interactions
    chartInteraction: (type, chartName = 'tide_curve') => trackEvent('chart_interaction', {
        interaction_type: type,
        chart_name: chartName
    }),

    // Form field changes
    formFieldChange: (fieldName, fieldValue) => trackEvent('form_field_change', {
        field_name: fieldName,
        field_value: String(fieldValue).substring(0, 50)
    }),

    // Data pipeline errors (Bulletproof Data Platform)
    dataFetchError: (source, errorMessage) => trackEvent('data_fetch_error', {
        data_source: source,        // 'conditions', 'buoy', 'weather', 'tide'
        error_message: errorMessage?.substring(0, 100) || 'Unknown error',
        page_location: window.location.href
    }),

    dataDegraded: (stationId, level, reason) => trackEvent('data_degraded', {
        station_id: stationId,
        degradation_level: level,   // 0, 1, 2, 3
        degradation_reason: reason?.substring(0, 100) || null
    }),

    validationFailed: (source, field, value) => trackEvent('validation_failed', {
        data_source: source,
        field_name: field,
        invalid_value: String(value).substring(0, 50)
    }),

    // Spot-based navigation
    spotChange: (spotSlug, spotName, region) => trackEvent('spot_change', {
        spot_slug: spotSlug,
        spot_name: spotName,
        region: region
    }),

    // Spectral data views
    viewSpectralBreakdown: (spotSlug) => trackEvent('view_spectral_breakdown', {
        spot_slug: spotSlug
    }),

    // Swell window filtering
    swellFiltered: (spotSlug, direction, reason) => trackEvent('swell_filtered', {
        spot_slug: spotSlug,
        swell_direction: direction,
        filter_reason: reason
    }),

    // Share actions (enhanced with rating context)
    share: (method, result, ratingScore = null, ratingCategory = null) => trackEvent('share', {
        share_method: method,    // 'native', 'twitter', 'facebook', 'whatsapp', 'email', 'sms', 'clipboard', 'clipboard_url', 'panel_open'
        share_result: result,    // 'success', 'error', 'view'
        rating_score: ratingScore,
        rating_category: ratingCategory
    })
};
